import React, { useCallback, useMemo, useState } from "react";
import {
  InnocodeButton,
  InnocodeInput,
  useDialog,
  useInnocodeLoadingOverlay,
} from "@intsoft/innocode-core";
import { containsXss } from "../../../utils/security/XssFilter";
import { ssoAdminApi } from "../api";
import styles from "../../../utils/modal/DetailModal.module.css";

const isInvalidText = (value) =>
  typeof value === "string" && value.trim() !== "" && containsXss(value);

const isFederationGroup = (group) =>
  String(group?.groupType || "")
    .trim()
    .toUpperCase() === "FEDERATION";

const normalizeGroups = (result) => {
  const items = Array.isArray(result?.content)
    ? result.content
    : Array.isArray(result?.items)
    ? result.items
    : Array.isArray(result?.data)
    ? result.data
    : Array.isArray(result)
    ? result
    : [];

  return items.filter((group) => isFederationGroup(group));
};

function TenantFederationRequestModal({ tenantOptions, onClose, onSaved }) {
  const { showDialog } = useDialog();
  const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [reason, setReason] = useState("");

  const sourceTenantOptions = useMemo(
    () => [{ value: "", label: "참여 테넌트 선택" }, ...tenantOptions],
    [tenantOptions]
  );

  const tenantMetaMap = useMemo(
    () =>
      tenantOptions.reduce((acc, option) => {
        acc[option.value] = option;
        return acc;
      }, {}),
    [tenantOptions]
  );

  const selectedTenantLabel = useMemo(
    () =>
      tenantOptions.find((option) => option.value === selectedTenantId)?.label ||
      "-",
    [selectedTenantId, tenantOptions]
  );

  const handleSearch = useCallback(async () => {
    if (!selectedTenantId) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 확인",
        message: "먼저 참여 테넌트를 선택해 주세요.",
        confirmText: "확인",
      });
      return;
    }

    if (!searchKeyword.trim()) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 확인",
        message: "서비스 그룹 키, 그룹명 또는 소유 테넌트명을 입력해 주세요.",
        confirmText: "확인",
      });
      return;
    }

    showLoading();

    try {
      const normalizedKeyword = searchKeyword.trim().toLowerCase();
      const result = await ssoAdminApi.fetchServiceGroups({
        groupType: "FEDERATION",
        keyword: searchKeyword.trim(),
        page: 0,
        size: 100,
      });

      const groups = normalizeGroups(result).filter((group) => {
        const targetTenantName =
          group.ownerTenantName ||
          tenantMetaMap[group.ownerTenantId]?.tenantName ||
          tenantMetaMap[group.ownerTenantId]?.label ||
          group.ownerTenantId ||
          "";

        return (
          group.ownerTenantId &&
          group.ownerTenantId !== selectedTenantId &&
          [group.groupKey, group.groupName, targetTenantName]
            .filter(Boolean)
            .some((value) =>
              String(value).toLowerCase().includes(normalizedKeyword)
            )
        );
      });

      setSearchResults(groups);
      setSelectedGroup(null);
    } catch (error) {
      setSearchResults([]);
      showDialog({
        iconType: "warning",
        type: "error",
        title: "검색 실패",
        message:
          error?.response?.data?.message ||
          "연합 요청 가능한 서비스 그룹을 불러오지 못했습니다.",
        confirmText: "확인",
      });
    } finally {
      hideLoading();
    }
  }, [
    hideLoading,
    searchKeyword,
    selectedTenantId,
    showDialog,
    showLoading,
    tenantMetaMap,
  ]);

  const handleSave = async () => {
    if (!selectedTenantId || !selectedGroup?.groupId || !selectedGroup?.ownerTenantId) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 확인",
        message: "참여 테넌트와 요청할 서비스 그룹을 선택해 주세요.",
        confirmText: "확인",
      });
      return;
    }

    if (isInvalidText(reason)) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 오류",
        message: "허용되지 않는 문자가 포함되어 있습니다.",
        confirmText: "확인",
      });
      return;
    }

    showLoading();

    try {
      await ssoAdminApi.createFederationRequest({
        sourceTenantId: selectedTenantId,
        targetTenantId: selectedGroup.ownerTenantId,
        groupId: selectedGroup.groupId,
        reason: reason.trim(),
      });

      showDialog({
        iconType: "check",
        type: "info",
        title: "요청 완료",
        message: "테넌트 연합 요청을 생성했습니다.",
        confirmText: "확인",
        onConfirm: () => {
          if (typeof onSaved === "function") {
            onSaved();
          }
          onClose();
        },
      });
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "요청 실패",
        message:
          error?.response?.data?.message ||
          "테넌트 연합 요청 중 오류가 발생했습니다.",
        confirmText: "확인",
      });
    } finally {
      hideLoading();
    }
  };

  return (
    <div>
      <div
        style={{
          marginBottom: "16px",
          padding: "12px 14px",
          border: "1px solid #d0d5dd",
          borderRadius: "10px",
          background: "#f8fafc",
          color: "#475467",
          fontSize: "13px",
          lineHeight: 1.6,
        }}
      >
        참여 테넌트가 연합을 요청하고, 서비스 그룹 소유 테넌트가 승인 또는 거절합니다.
        연합이 승인되어도 서비스는 그룹에 자동 포함되지 않으며, 실제 서비스 추가는
        별도 멤버 관리에서 진행해야 합니다.
      </div>

      <ul className={styles.layer__listWrap}>
        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>참여 테넌트</p>
          <select
            className={styles.layer__listInput}
            value={selectedTenantId}
            onChange={(event) => {
              setSelectedTenantId(event.target.value || "");
              setSearchResults([]);
              setSelectedGroup(null);
            }}
          >
            {sourceTenantOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </li>

        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>대상 그룹 검색</p>
          <div style={{ flex: 1, display: "flex", gap: "12px", minWidth: 0 }}>
            <InnocodeInput
              value={searchKeyword}
              onChange={(event) => setSearchKeyword(event.target.value)}
              placeholder="서비스 그룹 키, 그룹명, 소유 테넌트명으로 검색"
            />
            <InnocodeButton theme="line" designType="primary" onClick={handleSearch}>
              검색
            </InnocodeButton>
          </div>
        </li>

        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>검색 결과</p>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
            {searchResults.length > 0 ? (
              searchResults.map((group) => {
                const isSelected = selectedGroup?.groupId === group.groupId;
                const targetTenant = tenantMetaMap[group.ownerTenantId] || null;
                const targetTenantName =
                  group.ownerTenantName ||
                  targetTenant?.tenantName ||
                  targetTenant?.label ||
                  group.ownerTenantId ||
                  "-";
                const targetTenantImgUrl =
                  group.ownerTenantImgUrl || targetTenant?.tenantImgUrl || "";

                return (
                  <div
                    key={group.groupId}
                    style={{
                      border: `1px solid ${isSelected ? "#2563eb" : "#d0d5dd"}`,
                      borderRadius: "10px",
                      padding: "12px 14px",
                      background: isSelected ? "#eff6ff" : "#fff",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedGroup(group)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      {targetTenantImgUrl ? (
                        <img
                          src={targetTenantImgUrl}
                          alt={targetTenantName}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            objectFit: "cover",
                            border: "1px solid #d0d5dd",
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "#101828" }}>
                          {group.groupName || "-"}
                        </div>
                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#667085" }}>
                          그룹 키: {group.groupKey || "-"}
                        </div>
                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#344054" }}>
                          요청 흐름: {selectedTenantLabel} → {targetTenantName}
                        </div>
                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#475467" }}>
                          참여 테넌트 요청 · 소유 테넌트 승인/거절
                        </div>
                        {group.description ? (
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "#667085",
                              lineHeight: 1.5,
                              wordBreak: "break-word",
                            }}
                          >
                            {group.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  border: "1px dashed #d0d5dd",
                  borderRadius: "10px",
                  padding: "16px",
                  color: "#667085",
                  fontSize: "13px",
                }}
              >
                검색된 연합 서비스 그룹이 없습니다.
              </div>
            )}
          </div>
        </li>

        <li className={styles.layer__list}>
          <p className={styles.layer__listTit}>요청 사유</p>
          <InnocodeInput
            multiline={true}
            rows={4}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </li>
      </ul>

      <div className={styles.layer__saveBtnBox}>
        <InnocodeButton fullWidth={true} onClick={handleSave}>
          요청
        </InnocodeButton>
      </div>
    </div>
  );
}

export default TenantFederationRequestModal;
